import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Segmented, Select, Tag } from 'antd';
import { useSelector } from 'react-redux';
import { getProductDimensions } from '../../services/rapportService';
import {
  NdoePage, NdoePageHeader, NdoePeriodPicker,
  NdoeResponsiveTable, NdoeStatusBadge, NdoeStats, NdoeStatCard,
  NdoeError, NdoeLoading,
} from '../../components/ndoe';
import { money, days, pct } from './format';
import './rapports.css';

const AXES = [
  { value: 'couleur', label: 'Couleur' },
  { value: 'marque', label: 'Marque' },
  { value: 'categorie', label: 'Catégorie' },
  { value: 'matiere', label: 'Matière' },
  { value: 'hauteur', label: 'Hauteur de talon' },
];
const SORTS = [
  { value: 'naturel', label: 'Ordre du talon (plat → haut)' },
  { value: 'vendu', label: 'Quantité vendue' },
  { value: 'ecart', label: 'Plus sous-stockée' },
  { value: 'couverture', label: 'Couverture la plus courte' },
  { value: 'stock', label: 'Plus grand stock' },
  { value: 'libelle', label: 'A → Z' },
];
const DECISIONS = [
  { value: '', label: 'Toutes les décisions' },
  { value: 'renforcer', label: 'À renforcer' },
  { value: 'reduire', label: 'À réduire' },
  { value: 'maintenir', label: 'À maintenir' },
];

// Barre divergente identique à l'analyse pointures (classes pt-* partagées).
const EcartBar = ({ ecart }) => {
  const width = `${Math.min(50, Math.abs(ecart) * 5)}%`;
  return (
    <div className="pt-ecart">
      <span className={`num pt-ecart__val ${ecart > 0 ? 'is-plus' : ecart < 0 ? 'is-moins' : ''}`}>
        {ecart > 0 ? '+' : ''}{ecart}
      </span>
      <div className="pt-bar" aria-hidden="true">
        <span className="pt-bar__axe" />
        {ecart !== 0 && <i className={ecart > 0 ? 'is-plus' : 'is-moins'} style={{ width }} />}
      </div>
    </div>
  );
};

export default function AnalyseDimensions() {
  const user = useSelector((s) => s.user?.currentUser);
  const isAdmin = String(user?.role || '').toLowerCase() === 'admin';

  const [axe, setAxe] = useState('couleur');
  const [period, setPeriod] = useState({ period: 90 });
  const [sort, setSort] = useState('vendu');
  const [reco, setReco] = useState('');
  const [payload, setPayload] = useState({ meta: {}, summary: {}, data: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const apiParams = useMemo(() => ({
    axe,
    sort,
    recommandation: reco || undefined,
    period: period.period === 'custom' ? undefined : period.period,
    start_date: period.period === 'custom' ? period.start_date : undefined,
    end_date: period.period === 'custom' ? period.end_date : undefined,
  }), [axe, sort, reco, period]);

  // Garde de séquence : au changement rapide d'axe/tri/période, seule la
  // DERNIÈRE requête lancée a le droit d'écrire l'état — sinon une réponse en
  // retard affichait les données d'un axe sous l'en-tête d'un autre.
  const reqSeq = useRef(0);
  const load = useCallback(async () => {
    if (period.period === 'custom' && !(period.start_date && period.end_date)) { setLoading(false); return; }
    const seq = ++reqSeq.current;
    setLoading(true);
    try {
      const { data } = await getProductDimensions(apiParams);
      if (seq !== reqSeq.current) return;   // réponse périmée : ignorer
      setPayload(data);
      setError('');
    } catch (e) {
      if (seq !== reqSeq.current) return;
      setError(e.response?.data?.error || "Impossible de charger l'analyse par dimension.");
    } finally {
      if (seq === reqSeq.current) setLoading(false);
    }
  }, [apiParams, period]);
  useEffect(() => { if (isAdmin) load(); }, [isAdmin, load]);

  const s = payload.summary || {};
  const rows = payload.data || [];
  const axeLabel = (AXES.find((a) => a.value === axe) || AXES[0]).label.toLowerCase();

  const columns = [
    { title: AXES.find((a) => a.value === axe)?.label || 'Valeur', dataIndex: 'libelle', key: 'lib', render: (v, r) => (
      <div className="dm-lib"><b>{v}</b><small>{r.variantes} réf.</small></div>
    ) },
    { title: 'Vendu', key: 'vendu', align: 'right', render: (t, r) => (
      <div className="dm-num"><b className="num">{r.quantite_vendue}</b><small className="num">{pct(r.part_ventes)} des ventes</small></div>
    ) },
    { title: 'Stock', key: 'stock', align: 'right', render: (t, r) => (
      <div className="dm-num"><b className="num">{r.stock_actuel}</b><small className="num">{pct(r.part_stock)} du stock</small></div>
    ) },
    { title: 'Écart ventes / stock', key: 'ecart', render: (t, r) => <EcartBar ecart={r.ecart} /> },
    { title: 'CA', dataIndex: 'chiffre_affaires', key: 'ca', align: 'right', render: (v) => <span className="num">{money(v)}</span> },
    { title: 'Couverture', dataIndex: 'couverture_jours', key: 'cv', align: 'right', render: (v) => <span className="num">{days(v)}</span> },
    { title: 'Ruptures', dataIndex: 'ruptures', key: 'ru', align: 'right', render: (v) => <span className="num">{v || '—'}</span> },
    { title: 'Décision', key: 'reco', render: (t, r) => (
      <NdoeStatusBadge kind="custom" tone={r.recommandation_tone} label={r.recommandation_label} />
    ) },
    { title: 'Modèles qui tirent', key: 'top', render: (t, r) => (
      <div className="dm-top">
        {(r.top_produits || []).slice(0, 2).map((p, i) => <Tag key={i}>{p.nom} · {p.vendu}</Tag>)}
      </div>
    ) },
  ];

  if (!isAdmin) {
    return (
      <NdoePage>
        <NdoePageHeader eyebrow="Rapports" title="Analyse couleurs & marques" />
        <NdoeError title="Accès réservé" text="Ce rapport est réservé à l'administration." />
      </NdoePage>
    );
  }

  return (
    <NdoePage>
      <NdoePageHeader
        eyebrow="Rapports"
        title="Analyse par dimension"
        subtitle="Quelles couleurs, marques, catégories, matières ou HAUTEURS DE TALON renforcer ou réduire — en comparant la part des ventes à la part du stock."
      />

      <div className="dm-toolbar">
        <Segmented value={axe} onChange={(a) => {
          setAxe(a);
          setSort((prev) => (a === 'hauteur' ? 'naturel' : (prev === 'naturel' ? 'vendu' : prev)));
        }} options={AXES} />
        <NdoePeriodPicker value={period} onChange={setPeriod} />
        <Select value={sort} onChange={setSort}
          options={axe === 'hauteur' ? SORTS : SORTS.filter((o) => o.value !== 'naturel')}
          className="dm-f" title="Trier" />
        <Select value={reco} onChange={setReco} options={DECISIONS} className="dm-f" title="Filtrer par décision" />
      </div>

      {/* Perso sans dates : pas de bouton « Réessayer » (il ne ferait rien). */}
      {error ? (
        <NdoeError title="Erreur" text={error}
          onRetry={period.period === 'custom' && !(period.start_date && period.end_date) ? undefined : load} />
      ) : loading ? <NdoeLoading rows={5} /> : (
        <>
          <NdoeStats>
            <NdoeStatCard label="Paires vendues (période)" value={s.paires_vendues ?? 0} gold />
            <NdoeStatCard label="CA" value={money(s.chiffre_affaires)} />
            <NdoeStatCard label="À renforcer" value={s.a_renforcer ?? 0} />
            <NdoeStatCard label="À réduire" value={s.a_reduire ?? 0} />
          </NdoeStats>
          {s.vedette && <p className="dm-hint">La {axeLabel} qui vend le plus sur la période : <b>{s.vedette}</b>.</p>}
          {axe === 'hauteur' && s.couverture_qualification !== null && s.couverture_qualification !== undefined && (
            <p className={`dm-couv ${s.couverture_qualification < 60 ? 'is-bas' : ''}`}>
              {s.couverture_qualification >= 60
                ? <>✓ {s.couverture_qualification}% des paires vendues portent une hauteur renseignée — lecture fiable.</>
                : <>⚠️ Seulement {s.couverture_qualification}% des paires vendues portent une hauteur renseignée. Qualifiez plus de modèles (Catalogue → Hauteur de talon) pour affiner ce rapport.</>}
            </p>
          )}

          <div className="ndoe-card dm-block">
            <NdoeResponsiveTable
              data={rows}
              columns={columns}
              rowKey="libelle"
              pagination={{ pageSize: 12 }}
              scroll={{ x: 'max-content' }}
              empty={{ icon: '🎨', title: 'Aucune donnée sur la période' }}
            />
          </div>

          <p className="dm-note">
            Même lecture que l'analyse pointures : barre à droite (verte) = se vend plus que sa part de stock
            → renforcer ; barre à gauche (rouge) = occupe plus de stock qu'elle ne vend → réduire.
            « Ruptures » = références à zéro qui vendaient ; « couverture » = jours de stock au rythme récent.
          </p>
        </>
      )}
    </NdoePage>
  );
}
